/**
 * batch-69 vakantiecampagne (FO-519) - pure helpers rond de vakantiecampagne:
 * de inrichting van "Vakantie 2027", de status van een deelname uit de besluiten
 * per periode, de snelkeuze "hele week" in de wizard en de kleur van het
 * saldo-effect. Geen DB, geen klok: alles komt van buiten.
 *
 * Datums als `yyyy-mm-dd` zijn NL-kalenderdagen (Europe/Amsterdam). Instants
 * (Date) worden altijd als NL-kalenderdag gelezen (CLAUDE.md "Tijdzone").
 */

import { amsterdamAllDayEndToUtc, amsterdamWallclockToUtc } from './amsterdam-tijd'
import { isoWeek } from './intercompany-periode'
import { isoWekenVanVakantie } from './campagne-bezettingsconflict'

// ─── Inrichting "Vakantie 2027" ──────────────────────────────────────────────

/** De velden van een vakantiecampagne die de inrichting vastlegt. */
export interface VakantieCampagneInrichting {
  naam: string
  jaar: number
  /** De sleutel van de campagne: (VAKANTIE, jaar, vakantieType) is uniek. */
  vakantieType: 'GROTE_VAKANTIE'
  /** De bedoelde startdatum; bij `handmatigOpenen` zet de knop Vrijgeven het echte moment. */
  startDatum: Date
  /** Einde van de NL-dag: indienen kan tot en met die dag. */
  deadline: Date
  doelperiodeStart: Date
  /** Einde van de NL-dag: een periode tot en met die dag valt erbinnen. */
  doelperiodeEind: Date
  maxAantalVoorkeuren: number
  herkansingDagen: number
  autoGoedkeurenBijSaldo: boolean
  /**
   * Alleen de knop Vrijgeven opent de campagne; de cron opent hem nooit vanzelf.
   * Zo gaat er geen kaart of push uit voor de PO de inrichting (en de
   * prioriteitsregel) heeft bevestigd.
   */
  handmatigOpenen: boolean
  /** Afwijking van het herinneringsschema (`Campagne.herinneringSchema`). */
  herinneringSchema: { dagenVoorDeadline: number[]; dagelijksLaatsteDagen: number }
}

/**
 * Vakantie 2027 (spec 219 §2.A): start maandag 26-10-2026 08:00, deadline
 * 31-01-2027 (tot en met), doelperiode heel 2027 (hoofdvakantie en overige
 * wensen), maximaal 3 periodes, herkansing 14 dagen, push 14, 7 en 2 dagen voor
 * de deadline en dagelijks in de laatste 3 dagen. De prioriteitsregel hoort er
 * bewust NIET bij: dat is een beslispunt van de PO (A1) en de inrichting laat de
 * bestaande waarde staan.
 */
export const VAKANTIE_CAMPAGNE_2027: VakantieCampagneInrichting = {
  naam: 'Vakantie 2027',
  jaar: 2027,
  vakantieType: 'GROTE_VAKANTIE',
  startDatum: amsterdamWallclockToUtc('2026-10-26', '08:00'),
  deadline: amsterdamAllDayEndToUtc('2027-01-31'),
  // UTC-middernacht van 1 januari (de conventie van de beheer-UI): in NL is dat
  // dezelfde kalenderdag, en het jaar van de datum blijft 2027.
  doelperiodeStart: new Date(Date.UTC(2027, 0, 1)),
  doelperiodeEind: amsterdamAllDayEndToUtc('2027-12-31'),
  maxAantalVoorkeuren: 3,
  herkansingDagen: 14,
  autoGoedkeurenBijSaldo: false,
  handmatigOpenen: true,
  herinneringSchema: { dagenVoorDeadline: [14, 7, 2], dagelijksLaatsteDagen: 3 },
}

/**
 * Meldingen die maar een keer per campagne uitgaan en dus een nieuwe ronde
 * zouden blokkeren als ze uit een eerdere ronde nog in het dedup-spoor staan.
 * De momenten die aan de deadline hangen tellen per dag en blokkeren niet.
 */
export const INRICHTING_BLOKKERENDE_MELDINGEN = ['EERSTE_MELDING', 'HERINNERING_2W', 'HERINNERING_NIEUWJAAR'] as const

/** De bestaande campagne zoals de inrichting hem leest. */
export interface BestaandeVakantieCampagne {
  status: 'CONCEPT' | 'OPEN' | 'GESLOTEN' | 'AFGEROND'
  deletedAt: Date | null
  aantalDeelnames: number
  /** Typen uit het dedup-spoor (`CampagneNotificatie.type`) van deze campagne. */
  verstuurdeMeldingen: readonly string[]
  naam: string
  startDatum: Date
  deadline: Date
  doelperiodeStart: Date | null
  doelperiodeEind: Date | null
  maxAantalVoorkeuren: number
  herkansingDagen: number
  autoGoedkeurenBijSaldo: boolean
  handmatigOpenen: boolean
  herinneringSchema: unknown
  prioriteitsRegel: string
  /** Scope-filters: leeg = alle medewerkers in dienst (de doelgroep van spec 219). */
  entityIds: readonly string[]
  departmentIds: readonly string[]
}

export interface InrichtingsWijziging {
  veld: string
  oud: string
  nieuw: string
}

export type InrichtingsBesluit =
  | { actie: 'AANMAKEN' }
  | { actie: 'BIJWERKEN'; wijzigingen: InrichtingsWijziging[] }
  | { actie: 'AL_INGERICHT' }
  | { actie: 'WEIGEREN'; redenen: string[] }

const iso = (d: Date | null): string => (d ? d.toISOString() : 'leeg')

function schemaTekst(raw: unknown): string {
  if (raw == null) return 'standaard'
  return JSON.stringify(raw)
}

/**
 * Wat de inrichting met een bestaande campagne mag doen. Alleen een campagne die
 * nog niet loopt (CONCEPT, of GESLOTEN zonder een enkele deelname) wordt
 * bijgewerkt, en dan terug naar CONCEPT. Loopt hij (OPEN), is hij afgerond, zijn
 * er al deelnames, of staan er eenmalige meldingen van een eerdere ronde in het
 * spoor, dan weigert de inrichting met de reden: daar beslist een mens.
 */
export function beoordeelVakantieInrichting(
  bestaand: BestaandeVakantieCampagne | null,
  doel: VakantieCampagneInrichting,
): InrichtingsBesluit {
  if (!bestaand) return { actie: 'AANMAKEN' }
  const redenen: string[] = []
  if (bestaand.deletedAt) {
    redenen.push('De campagne is verwijderd (soft-delete). Een nieuwe kan niet naast hem bestaan (jaar en type zijn uniek); herstel hem eerst bewust.')
  }
  if (bestaand.status === 'OPEN') {
    redenen.push('De campagne staat open: medewerkers kunnen al indienen. De inrichting verandert niets aan een lopende campagne; pas hem aan in Verlof > Campagnes.')
  }
  if (bestaand.status === 'AFGEROND') {
    redenen.push('De campagne is afgerond. Een afgeronde campagne wordt niet opnieuw ingericht.')
  }
  if (bestaand.aantalDeelnames > 0) {
    redenen.push(
      `De campagne heeft al ${bestaand.aantalDeelnames} ${bestaand.aantalDeelnames === 1 ? 'deelname' : 'deelnames'}. Opnieuw inrichten zou ingediende wensen of keuzes van medewerkers raken.`,
    )
  }
  const blokkerend = [...new Set(bestaand.verstuurdeMeldingen)].filter((t) =>
    (INRICHTING_BLOKKERENDE_MELDINGEN as readonly string[]).includes(t),
  )
  if (blokkerend.length > 0) {
    redenen.push(
      `In het meldingenspoor staan al eenmalige meldingen van een eerdere ronde (${blokkerend.join(', ')}). Die zouden de startmelding of herinneringen van de nieuwe ronde blokkeren.`,
    )
  }
  if (redenen.length > 0) return { actie: 'WEIGEREN', redenen }

  const wijzigingen: InrichtingsWijziging[] = []
  const vergelijk = (veld: string, oud: string, nieuw: string) => {
    if (oud !== nieuw) wijzigingen.push({ veld, oud, nieuw })
  }
  vergelijk('status', bestaand.status, 'CONCEPT')
  vergelijk('naam', bestaand.naam, doel.naam)
  vergelijk('startDatum', iso(bestaand.startDatum), iso(doel.startDatum))
  vergelijk('deadline', iso(bestaand.deadline), iso(doel.deadline))
  vergelijk('doelperiodeStart', iso(bestaand.doelperiodeStart), iso(doel.doelperiodeStart))
  vergelijk('doelperiodeEind', iso(bestaand.doelperiodeEind), iso(doel.doelperiodeEind))
  vergelijk('maxAantalVoorkeuren', String(bestaand.maxAantalVoorkeuren), String(doel.maxAantalVoorkeuren))
  vergelijk('herkansingDagen', String(bestaand.herkansingDagen), String(doel.herkansingDagen))
  vergelijk('autoGoedkeurenBijSaldo', String(bestaand.autoGoedkeurenBijSaldo), String(doel.autoGoedkeurenBijSaldo))
  vergelijk('handmatigOpenen', String(bestaand.handmatigOpenen), String(doel.handmatigOpenen))
  vergelijk('herinneringSchema', schemaTekst(bestaand.herinneringSchema), schemaTekst(doel.herinneringSchema))
  // Doelgroep: alle medewerkers in dienst, zonder BV- of afdelingsfilter.
  vergelijk('entityIds', bestaand.entityIds.length ? bestaand.entityIds.join(',') : 'alle', 'alle')
  vergelijk('departmentIds', bestaand.departmentIds.length ? bestaand.departmentIds.join(',') : 'alle', 'alle')
  return wijzigingen.length === 0 ? { actie: 'AL_INGERICHT' } : { actie: 'BIJWERKEN', wijzigingen }
}

// ─── Vervangen periodes (herkansing) ─────────────────────────────────────────

/**
 * Een periode met een besluit wordt nooit verwijderd. Geeft de medewerker in een
 * herkansing een nieuwe periode op, dan schuift de afgewezen periode uit de
 * prioriteitsvolgorde naar een volgnummer vanaf deze grens (de nieuwe periode neemt
 * zijn plek in) en blijft hij met besluit, reden en beoordelaar bewaard. De wizard
 * en de API gebruiken prioriteiten 1 tot en met 5.
 */
export const VERVANGEN_VOLGNUMMER_VANAF = 100

/** Is deze periode in een herkansing vervangen (afgewezen en uit de volgorde geschoven)? */
export function isVervangenPeriode(volgnummer: number): boolean {
  return volgnummer >= VERVANGEN_VOLGNUMMER_VANAF
}

/** De volgnummers voor periodes die nu uit de volgorde schuiven: na de hoogste die er al staat. */
export function vervangenVolgnummers(bestaand: readonly number[], aantal: number): number[] {
  const start = Math.max(VERVANGEN_VOLGNUMMER_VANAF, ...bestaand.map((n) => n + 1))
  return Array.from({ length: aantal }, (_, i) => start + i)
}

// ─── Besluit per periode -> status van de deelname ───────────────────────────

/**
 * Besluit over één periode (`CampagneVakantieVoorkeur.besluit`); null = nog niet beoordeeld.
 * batch-70 (FO-525 §D14): IN_OVERLEG (nog in behandeling, we gaan in overleg), VERVANGEN
 * (na indienen gewijzigd: de nieuwe wens neemt het over) en INGETROKKEN (door de
 * medewerker). Die laatste twee zijn geschiedenis en tellen niet mee in de status.
 */
export type VakantiePeriodeBesluit = 'GOEDGEKEURD' | 'AFGEWEZEN' | 'WACHTLIJST' | 'IN_OVERLEG' | 'VERVANGEN' | 'INGETROKKEN'

/** Een besluit dat geschiedenis is (de wens doet niet meer mee). */
export function isAfgeslotenWens(besluit: VakantiePeriodeBesluit | null): boolean {
  return besluit === 'VERVANGEN' || besluit === 'INGETROKKEN'
}

/**
 * De status van een deelname nadat er over (een deel van) de periodes is
 * beslist. Zolang er een periode open staat (of in overleg is) loopt de
 * beoordeling; daarna wint een goedkeuring (de medewerker heeft vakantie), dan de
 * wachtlijst, en alleen als alles is afgewezen is de deelname afgewezen. Vervangen
 * en ingetrokken wensen tellen niet mee.
 */
export function bepaalDeelnameStatusUitBesluiten(
  besluiten: ReadonlyArray<VakantiePeriodeBesluit | null>,
): 'IN_BEOORDELING' | 'GOEDGEKEURD' | 'WACHTLIJST' | 'AFGEWEZEN' {
  const actief = besluiten.filter((b) => !isAfgeslotenWens(b))
  if (actief.length === 0 || actief.some((b) => b === null || b === 'IN_OVERLEG')) return 'IN_BEOORDELING'
  if (actief.includes('GOEDGEKEURD')) return 'GOEDGEKEURD'
  if (actief.includes('WACHTLIJST')) return 'WACHTLIJST'
  return 'AFGEWEZEN'
}

/** Kan er (nog) over deze periode worden beslist? Open, op de wachtlijst of in overleg. */
export function isPeriodeBeoordeelbaar(besluit: VakantiePeriodeBesluit | null): boolean {
  return besluit === null || besluit === 'WACHTLIJST' || besluit === 'IN_OVERLEG'
}

/**
 * De besluiten in gewone taal: "2 goedgekeurd, 1 afgewezen, 1 nog te beoordelen".
 * Een plek voor de kaart, de uitslagpagina en de melding (spec 219 §B5). Vervangen
 * en ingetrokken wensen staan er niet in.
 */
export function besluitSamenvatting(besluiten: ReadonlyArray<VakantiePeriodeBesluit | null>): string {
  const tel = { GOEDGEKEURD: 0, AFGEWEZEN: 0, WACHTLIJST: 0, IN_OVERLEG: 0, OPEN: 0 }
  for (const b of besluiten) {
    if (isAfgeslotenWens(b)) continue
    tel[(b ?? 'OPEN') as keyof typeof tel]++
  }
  const delen: string[] = []
  if (tel.GOEDGEKEURD) delen.push(`${tel.GOEDGEKEURD} goedgekeurd`)
  if (tel.AFGEWEZEN) delen.push(`${tel.AFGEWEZEN} afgewezen`)
  if (tel.WACHTLIJST) delen.push(`${tel.WACHTLIJST} op de wachtlijst`)
  if (tel.IN_OVERLEG) delen.push(`${tel.IN_OVERLEG} in overleg`)
  if (tel.OPEN) delen.push(`${tel.OPEN} nog te beoordelen`)
  return delen.join(', ')
}

// ─── Saldo-effect ────────────────────────────────────────────────────────────

export type SaldoToon = 'GROEN' | 'ORANJE' | 'ROOD'

/**
 * Kleur van het saldo na een periode. Groen: het saldo blijft toereikend.
 * Oranje: tekort, maar indienen mag; de wens wordt "onder voorbehoud"
 * beoordeeld. Rood: tekort en deze campagne laat geen tekort toe
 * (`autoGoedkeurenBijSaldo`), indienen lukt dan niet.
 */
export function saldoToon(saldoNa: number, tekortToegestaan: boolean): SaldoToon {
  if (saldoNa >= 0) return 'GROEN'
  return tekortToegestaan ? 'ORANJE' : 'ROOD'
}

// ─── Hele weken (snelkeuze in de wizard) ─────────────────────────────────────

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/

function ymdNaarUtc(ymd: string): Date | null {
  const m = YMD.exec(ymd)
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return Number.isNaN(d.getTime()) ? null : d
}

function utcNaarYmd(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function dagMaand(ymd: string): string {
  return `${ymd.slice(8, 10)}-${ymd.slice(5, 7)}`
}

export interface HeleWeekOptie {
  jaar: number
  week: number
  /** Maandag van de week (of de eerste dag binnen de periode). */
  van: string
  /** Zondag van de week (of de laatste dag binnen de periode). */
  tot: string
  /** "Week 30 (26-07 t/m 01-08)" */
  label: string
}

/**
 * De ISO-weken die een periode raakt, als snelkeuze "hele week": maandag t/m
 * zondag, afgekapt op de periode (de eerste en de laatste week kunnen korter
 * zijn). `van` en `tot` zijn NL-kalenderdagen als `yyyy-mm-dd`.
 */
export function heleWekenInPeriode(van: string, tot: string): HeleWeekOptie[] {
  const start = ymdNaarUtc(van)
  const eind = ymdNaarUtc(tot)
  if (!start || !eind || eind < start) return []
  const maandag = new Date(start)
  maandag.setUTCDate(maandag.getUTCDate() - ((maandag.getUTCDay() + 6) % 7))
  const uit: HeleWeekOptie[] = []
  while (maandag <= eind) {
    const zondag = new Date(maandag)
    zondag.setUTCDate(zondag.getUTCDate() + 6)
    const weekVan = maandag < start ? start : maandag
    const weekTot = zondag > eind ? eind : zondag
    const w = isoWeek(new Date(Date.UTC(maandag.getUTCFullYear(), maandag.getUTCMonth(), maandag.getUTCDate(), 12)))
    const vanYmd = utcNaarYmd(weekVan)
    const totYmd = utcNaarYmd(weekTot)
    uit.push({ jaar: w.jaar, week: w.week, van: vanYmd, tot: totYmd, label: `Week ${w.week} (${dagMaand(vanYmd)} t/m ${dagMaand(totYmd)})` })
    maandag.setUTCDate(maandag.getUTCDate() + 7)
  }
  return uit
}

/**
 * Kort weeklabel van een periode: "wk 30", "wk 30-31" of over de jaargrens
 * "wk 52-1". Op NL-kalenderdagen.
 */
export function weekLabelVanPeriode(start: Date, eind: Date): string {
  const weken = isoWekenVanVakantie(start, eind)
  if (weken.length === 0) return ''
  const eerste = weken[0]!
  const laatste = weken[weken.length - 1]!
  return weken.length === 1 ? `wk ${eerste.week}` : `wk ${eerste.week}-${laatste.week}`
}
